import { useEffect, useMemo, useState } from "react";
import {
    collection,
    getDocs,
    deleteDoc,
    doc
} from "firebase/firestore";

import { db } from "../../firebase/firebase";

import Sidebar from "../Sidebar";
import AdminNavbar from "../AdminNavbar";
import Footer from "../../components/common/Footer";

import "../../assets/css/admin/feedbackList.css";

function FeedbackList() {

    const [feedbacks, setFeedbacks] = useState([]);

    const [loading, setLoading] = useState(true);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);

    /*=========================================================
                        FILTER STATES
    =========================================================*/

    const [searchText, setSearchText] = useState("");

    const [ratingFilter, setRatingFilter] = useState("all");

    const [monthFilter, setMonthFilter] = useState("all");

    const [dateFilter, setDateFilter] = useState("");

    /*=========================================================
                        LOAD FEEDBACKS
    =========================================================*/

    useEffect(() => {

        loadFeedbacks();

    }, []);

    const loadFeedbacks = async () => {

        try {

            setLoading(true);

            const snapshot = await getDocs(
                collection(db, "feedbacks")
            );

            const list = [];

            snapshot.forEach((item) => {

                const data = item.data();

                list.push({

                    id: item.id,

                    ...data

                });

            });

            setFeedbacks(list);

        }

        catch (error) {

            console.error(
                "Error loading feedbacks:",
                error
            );

        }

        finally {

            setLoading(false);

        }

    };

    /*=========================================================
                    CONVERT FIREBASE DATE
    =========================================================*/

    const getFeedbackDate = (item) => {

        if (!item.createdAt) {

            return null;

        }

        try {

            if (
                typeof item.createdAt === "object" &&
                typeof item.createdAt.toDate === "function"
            ) {

                return item.createdAt.toDate();

            }

            if (item.createdAt instanceof Date) {

                return item.createdAt;

            }

            const date = new Date(item.createdAt);

            if (!isNaN(date.getTime())) {

                return date;

            }

        }

        catch (error) {

            console.log(
                "Date conversion error:",
                error
            );

        }

        return null;

    };

    /*=========================================================
                    FILTER FEEDBACKS
    =========================================================*/

    const filteredFeedbacks = useMemo(() => {

        let result = [...feedbacks];

        /* SEARCH */

        if (searchText.trim() !== "") {

            const search = searchText
                .toLowerCase()
                .trim();

            result = result.filter((item) => {

                const name =
                    item.name
                        ?.toLowerCase() || "";

                const email =
                    item.email
                        ?.toLowerCase() || "";

                const message =
                    item.message
                        ?.toLowerCase() || "";

                return (

                    name.includes(search) ||

                    email.includes(search) ||

                    message.includes(search)

                );

            });

        }

        /* RATING */

        if (ratingFilter !== "all") {

            result = result.filter(

                (item) =>
                    String(item.rating) ===
                    String(ratingFilter)

            );

        }

        /* MONTH */

        if (monthFilter !== "all") {

            result = result.filter((item) => {

                const date =
                    getFeedbackDate(item);

                if (!date) {

                    return false;

                }

                const month =
                    String(
                        date.getMonth() + 1
                    ).padStart(2, "0");

                return month === monthFilter;

            });

        }

        /* SPECIFIC DATE */

        if (dateFilter !== "") {

            result = result.filter((item) => {

                const date =
                    getFeedbackDate(item);

                if (!date) {

                    return false;

                }

                const year =
                    date.getFullYear();

                const month =
                    String(
                        date.getMonth() + 1
                    ).padStart(2, "0");

                const day =
                    String(
                        date.getDate()
                    ).padStart(2, "0");

                const formattedDate =
                    `${year}-${month}-${day}`;

                return formattedDate === dateFilter;

            });

        }

        /* LATEST FIRST */

        result.sort((a, b) => {

            const dateA =
                getFeedbackDate(a);

            const dateB =
                getFeedbackDate(b);

            if (!dateA && !dateB) {

                return 0;

            }

            if (!dateA) {

                return 1;

            }

            if (!dateB) {

                return -1;

            }

            return dateB - dateA;

        });

        return result;

    }, [

        feedbacks,

        searchText,

        ratingFilter,

        monthFilter,

        dateFilter

    ]);

    /*=========================================================
                        CLEAR FILTERS
    =========================================================*/

    const clearFilters = () => {

        setSearchText("");

        setRatingFilter("all");

        setMonthFilter("all");

        setDateFilter("");

    };

    /*=========================================================
                        DELETE
    =========================================================*/

    const deleteFeedback = (id) => {

        setSelectedFeedbackId(id);

        setShowDeleteModal(true);

    };

    /*=========================================================
                    CONFIRM DELETE
    =========================================================*/

    const confirmDeleteFeedback = async () => {

        if (!selectedFeedbackId) {

            return;

        }

        try {

            await deleteDoc(

                doc(
                    db,
                    "feedbacks",
                    selectedFeedbackId
                )

            );

            setShowDeleteModal(false);

            setSelectedFeedbackId(null);

            await loadFeedbacks();

        }

        catch (error) {

            console.error(
                "Error deleting feedback:",
                error
            );

        }

    };

    /*=========================================================
                    FORMAT DATE
    =========================================================*/

    const formatDate = (item) => {

        const date =
            getFeedbackDate(item);

        if (!date) {

            return "—";

        }

        return date.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric"
            }
        );

    };

    /*=========================================================
                            JSX
    =========================================================*/

    return (

        <>

            {/*=================================================
                        DELETE MODAL
            =================================================*/}

            {showDeleteModal && (

                <div className="delete-overlay">

                    <div className="delete-modal">

                        <div className="delete-modal-icon">
                            🗑️
                        </div>

                        <h2>
                            Delete Feedback?
                        </h2>

                        <p>
                            Are you sure you want to delete
                            this feedback? This action cannot
                            be undone.
                        </p>

                        <div className="delete-actions">

                            <button
                                className="cancel-btn"
                                onClick={() => {

                                    setShowDeleteModal(false);

                                    setSelectedFeedbackId(null);

                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="confirm-btn"
                                onClick={confirmDeleteFeedback}
                            >
                                Delete
                            </button>

                        </div>

                    </div>

                </div>

            )}

            {/*=================================================
                        ADMIN PAGE
            =================================================*/}

            <div className="feedback-admin-page">

                <AdminNavbar />

                <div className="feedback-admin-main">

                    <Sidebar />

                    <div className="feedback-admin-content">

                        {/*=================================================
                                    HEADER
                        =================================================*/}

                        <div className="feedback-header">

                            <div>

                                <span className="feedback-eyebrow">
                                    STORY LINE STUDIOS
                                </span>

                                <h1 className="feedback-admin-title">
                                    ⭐ Client Feedback
                                </h1>

                                <p className="feedback-admin-subtitle">
                                    Manage, review and monitor customer
                                    feedback submitted through your website.
                                </p>

                            </div>

                            <div className="feedback-count-card">

                                <span>
                                    Total Feedback
                                </span>

                                <strong>
                                    {feedbacks.length}
                                </strong>

                            </div>

                        </div>

                        {/*=================================================
                                FILTER SECTION
                        =================================================*/}

                        <div className="feedback-filter-card">

                            <div className="filter-heading">

                                <div>

                                    <h3>
                                        Feedback Filters
                                    </h3>

                                    <span>
                                        Find customer reviews quickly
                                    </span>

                                </div>

                                <button
                                    className="clear-filter-btn"
                                    onClick={clearFilters}
                                >
                                    ↻ Clear Filters
                                </button>

                            </div>

                            <div className="feedback-filters">

                                {/* SEARCH */}

                                <div className="filter-group search-group">

                                    <label>
                                        Search Feedback
                                    </label>

                                    <div className="search-input-wrapper">

                                        <span>
                                            🔎
                                        </span>

                                        <input
                                            type="text"
                                            placeholder="Name, email or message..."
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(
                                                    e.target.value
                                                )
                                            }
                                        />

                                    </div>

                                </div>

                                {/* RATING */}

                                <div className="filter-group">

                                    <label>
                                        Rating
                                    </label>

                                    <select
                                        value={ratingFilter}
                                        onChange={(e) =>
                                            setRatingFilter(
                                                e.target.value
                                            )
                                        }
                                    >

                                        <option value="all">
                                            All Ratings
                                        </option>

                                        <option value="5">
                                            ⭐⭐⭐⭐⭐ 5 Stars
                                        </option>

                                        <option value="4">
                                            ⭐⭐⭐⭐ 4 Stars
                                        </option>

                                        <option value="3">
                                            ⭐⭐⭐ 3 Stars
                                        </option>

                                        <option value="2">
                                            ⭐⭐ 2 Stars
                                        </option>

                                        <option value="1">
                                            ⭐ 1 Star
                                        </option>

                                    </select>

                                </div>

                                {/* MONTH */}

                                <div className="filter-group">

                                    <label>
                                        Month
                                    </label>

                                    <select
                                        value={monthFilter}
                                        onChange={(e) =>
                                            setMonthFilter(
                                                e.target.value
                                            )
                                        }
                                    >

                                        <option value="all">
                                            All Months
                                        </option>

                                        <option value="01">
                                            January
                                        </option>

                                        <option value="02">
                                            February
                                        </option>

                                        <option value="03">
                                            March
                                        </option>

                                        <option value="04">
                                            April
                                        </option>

                                        <option value="05">
                                            May
                                        </option>

                                        <option value="06">
                                            June
                                        </option>

                                        <option value="07">
                                            July
                                        </option>

                                        <option value="08">
                                            August
                                        </option>

                                        <option value="09">
                                            September
                                        </option>

                                        <option value="10">
                                            October
                                        </option>

                                        <option value="11">
                                            November
                                        </option>

                                        <option value="12">
                                            December
                                        </option>

                                    </select>

                                </div>

                                {/* DATE */}

                                <div className="filter-group">

                                    <label>
                                        Specific Date
                                    </label>

                                    <input
                                        type="date"
                                        value={dateFilter}
                                        onChange={(e) =>
                                            setDateFilter(
                                                e.target.value
                                            )
                                        }
                                    />

                                </div>

                            </div>

                        </div>

                        {/*=================================================
                                RESULT INFORMATION
                        =================================================*/}

                        <div className="feedback-result-bar">

                            <div>

                                Showing{" "}

                                <strong>
                                    {filteredFeedbacks.length}
                                </strong>{" "}

                                of{" "}

                                <strong>
                                    {feedbacks.length}
                                </strong>{" "}

                                feedback entries

                            </div>

                            {(searchText ||
                                ratingFilter !== "all" ||
                                monthFilter !== "all" ||
                                dateFilter) && (

                                <span className="filter-active">
                                    ● Filters Active
                                </span>

                            )}

                        </div>

                        {/*=================================================
                                TABLE
                        =================================================*/}

                        <div className="feedback-table-wrapper">

                            <table className="feedback-admin-table">

                                <thead>

                                    <tr>

                                        <th>
                                            Name
                                        </th>

                                        <th>
                                            Email
                                        </th>

                                        <th>
                                            Rating
                                        </th>

                                        <th>
                                            Message
                                        </th>

                                        <th>
                                            Submitted
                                        </th>

                                        <th>
                                            Action
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {loading ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className="table-status"
                                            >
                                                Loading feedback...
                                            </td>

                                        </tr>

                                    ) : filteredFeedbacks.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className="table-status"
                                            >

                                                <div className="empty-feedback">

                                                    <div>
                                                        ⭐
                                                    </div>

                                                    <h3>
                                                        No Feedback Found
                                                    </h3>

                                                    <p>
                                                        Try changing your
                                                        filters or search
                                                        criteria.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : (

                                        filteredFeedbacks.map((item) => (

                                            <tr key={item.id}>

                                                <td data-label="Name">

                                                    <div className="feedback-name">

                                                        <span className="feedback-avatar">
                                                            {item.name
                                                                ?.charAt(0)
                                                                ?.toUpperCase() || "?"}
                                                        </span>

                                                        <strong>
                                                            {item.name || "—"}
                                                        </strong>

                                                    </div>

                                                </td>

                                                <td data-label="Email">

                                                    <span className="feedback-email">
                                                        {item.email || "—"}
                                                    </span>

                                                </td>

                                                <td
                                                    data-label="Rating"
                                                    className="feedback-rating"
                                                >

                                                    {"⭐".repeat(
                                                        Number(item.rating) || 0
                                                    )}

                                                </td>

                                                <td
                                                    data-label="Message"
                                                    className="feedback-message"
                                                >

                                                    {item.message || "—"}

                                                </td>

                                                <td data-label="Submitted">

                                                    <span className="feedback-date">
                                                        {formatDate(item)}
                                                    </span>

                                                </td>

                                                <td
                                                    data-label="Action"
                                                    className="feedback-action"
                                                >

                                                    <button
                                                        className="feedback-delete-btn"
                                                        onClick={() =>
                                                            deleteFeedback(
                                                                item.id
                                                            )
                                                        }
                                                    >
                                                        🗑 Delete
                                                    </button>

                                                </td>

                                            </tr>

                                        ))

                                    )}

                                </tbody>

                            </table>

                        </div>

                    </div>

                </div>

            </div>

            <Footer />

        </>

    );

}

export default FeedbackList;